/**
 * Clip lifecycle, timeline view state and playback.
 *
 * Every clip mutation goes through shared `clipOps`; every evaluation goes through the shared
 * evaluator (plan D11), so editor preview, runtime playback and GLB baking cannot drift.
 */

import {
  changeFps,
  clipDurationSeconds,
  createEmptyClip,
  DEFAULT_FPS,
  evaluateHipsY,
  evaluatePose,
  identityQuat,
  isAppError,
  normalizeClip,
  quatEquals,
  setDuration as setClipDuration,
  wrapFrame,
} from '../../shared';
import type { CanonicalAnimationClip, CanonicalBoneId } from '../../shared';
import type { EditorState, VisibleBonesFilter } from '../types';
import {
  PLAYBACK_SPEED_MAX,
  PLAYBACK_SPEED_MIN,
  TIMELINE_ZOOM_MAX,
  TIMELINE_ZOOM_MIN,
} from '../types';
import {
  commitClipEdit,
  evaluatedLive,
  POSE_EPSILON,
  reportError,
  showToast,
  type ActionContext,
} from './context';
import { deactivateAllIk } from './ik';

export interface ClipActions {
  newClip(name: string): void;
  openClip(clip: CanonicalAnimationClip): void;
  setClipName(name: string): void;
  setFps(fps: number): void;
  setDuration(durationFrames: number): void;
  setLoop(loop: boolean): void;
  setPlayhead(frame: number): void;
  play(): void;
  pause(): void;
  togglePlay(): void;
  stop(): void;
  /**
   * Playback rate multiplier (1 = real time), clamped to
   * [PLAYBACK_SPEED_MIN, PLAYBACK_SPEED_MAX]. A non-finite value is ignored. Preview only —
   * it never touches the clip.
   */
  setPlaybackSpeed(speed: number): void;
  /** Advance playback by real time. NOT undoable; the render loop calls it every frame. */
  tick(deltaSeconds: number): void;
  setSnap(snap: boolean): void;
  setZoom(zoom: number): void;
  setScrollFrame(frame: number): void;
  setVisibleBones(filter: VisibleBonesFilter): void;
}

/**
 * True when the live posture holds anything the clip does not already produce at `frame` —
 * a rotation OR the hips height, which is un-keyed work in exactly the same sense.
 */
function hasUnkeyedPose(state: EditorState, frame: number): boolean {
  if (!state.clip) return false;
  if (Math.abs(state.liveHipsY - evaluateHipsY(state.clip, frame)) > POSE_EPSILON) return true;
  const evaluated = evaluatePose(state.clip, frame);
  for (const bone of Object.keys(state.livePose) as CanonicalBoneId[]) {
    const live = state.livePose[bone];
    if (!live) continue;
    if (!quatEquals(live, evaluated[bone] ?? identityQuat(), POSE_EPSILON)) return true;
  }
  return false;
}

export function createClipActions(ctx: ActionContext): ClipActions {
  /** State a freshly opened/created clip starts from. */
  const openState = (clip: CanonicalAnimationClip, dirty: boolean): Partial<EditorState> => ({
    clip,
    clipDirty: dirty,
    keySelection: [],
    ...evaluatedLive(clip, 0),
    // The ONE playhead write that does not spread the previous state, so `speed` has to be
    // carried across by hand: it is a workspace preference, not clip data, and dropping it
    // would silently snap playback back to 1× (or, if the field were optional, NaN the tick).
    playhead: { frame: 0, playing: false, loop: clip.loop, speed: ctx.getState().playhead.speed },
  });

  const gotoFrame = (frame: number): void => {
    const state = ctx.getState();
    if (!state.clip) {
      ctx.set({ playhead: { ...state.playhead, frame: 0 }, livePose: {}, liveHipsY: 0 });
      return;
    }
    let target = wrapFrame(state.clip, frame, false);
    if (!state.playhead.playing) target = Math.round(target);
    ctx.set({
      playhead: { ...state.playhead, frame: target },
      ...evaluatedLive(state.clip, target),
    });
  };

  return {
    newClip(name): void {
      const clip = createEmptyClip(name, DEFAULT_FPS);
      // Undo entries reference the previous clip, so the document change resets the stack.
      ctx.history.clear();
      deactivateAllIk(ctx);
      ctx.set(openState(clip, true));
    },

    openClip(clip): void {
      const copy = normalizeClip(clip);
      ctx.history.clear();
      deactivateAllIk(ctx);
      ctx.set(openState(copy, false));
    },

    /**
     * Names are trimmed, never empty, and unique within the library. The rename path enforced
     * that already; the edit-then-save path did not, so a clip could be saved blank or as the
     * third "Walk" in a library where `play("Walk")` then silently picks one of them (audit P1).
     * Rejecting here means the user finds out at the input, not at the export.
     */
    setClipName(name): void {
      const state = ctx.getState();
      if (!state.clip) return;
      const trimmed = name.trim();
      if (trimmed === state.clip.name) return;
      if (trimmed.length === 0) {
        showToast(ctx, 'error', 'An animation name cannot be empty.');
        return;
      }
      const clash = state.library.clips.find((c) => c.name === trimmed && c.id !== state.clip?.id);
      if (clash) {
        showToast(ctx, 'error', `The library already has an animation called "${trimmed}".`);
        return;
      }
      ctx.set({
        clip: { ...state.clip, name: trimmed, updatedAt: Date.now() },
        clipDirty: true,
      });
    },

    setFps(fps): void {
      const state = ctx.getState();
      if (!state.clip) return;
      let result: { clip: CanonicalAnimationClip; droppedKeys: number };
      try {
        result = changeFps(state.clip, fps);
      } catch (error) {
        reportError(ctx, isAppError(error) ? error.message : String(error));
        return;
      }
      if (result.clip === state.clip) return;

      // A retime moves the instant a frame number points at, so the playhead is rescaled by the
      // same ratio as the keys and the pose is re-derived at the new frame. Both are DERIVED from
      // the clip, so both ride the clip's own undo entry (the 6th and 7th args) instead of a bare
      // `ctx.set` after the commit. That bare set was audit §2.2 #2: undo restored the 30 fps clip
      // but left the playhead on the 60 fps frame and `livePose` on the 60 fps rotation, and the
      // next `K` wrote that rotation over the user's original keyframe — silently.
      const ratio = result.clip.fps / state.clip.fps;
      const frame = wrapFrame(result.clip, Math.round(state.playhead.frame * ratio), false);
      const live = evaluatedLive(result.clip, frame);
      commitClipEdit(ctx, 'Change fps', result.clip, {
        nextSelection: [],
        nextLivePose: live.livePose,
        nextLiveHipsY: live.liveHipsY,
        nextFrame: frame,
      });
      if (result.droppedKeys > 0) {
        showToast(
          ctx,
          'info',
          `${result.droppedKeys} keyframe${result.droppedKeys === 1 ? '' : 's'} dropped by the fps change.`,
        );
      }
    },

    setDuration(durationFrames): void {
      const state = ctx.getState();
      if (!state.clip) return;
      let next: CanonicalAnimationClip;
      try {
        next = setClipDuration(state.clip, durationFrames);
      } catch (error) {
        reportError(ctx, isAppError(error) ? error.message : String(error));
        return;
      }
      if (!commitClipEdit(ctx, 'Set duration', next)) return;
      gotoFrame(ctx.getState().playhead.frame);
    },

    setLoop(loop): void {
      const state = ctx.getState();
      if (state.playhead.loop === loop && (!state.clip || state.clip.loop === loop)) return;
      const patch: Partial<EditorState> = { playhead: { ...state.playhead, loop } };
      if (state.clip && state.clip.loop !== loop) {
        patch.clip = { ...state.clip, loop, updatedAt: Date.now() };
        patch.clipDirty = true;
      }
      ctx.set(patch);
    },

    /**
     * A user scrub. Two side effects beyond moving the playhead, both wave-5 review fixes:
     *  - IK activation is momentary, so every active chain is released (the handles re-glue to
     *    their end bone instead of pulling towards a target placed at another frame);
     *  - an unkeyed pose would otherwise be thrown away silently, so it is offered back as ONE
     *    undo entry plus an info toast.
     */
    setPlayhead(frame): void {
      const before = ctx.getState();
      const previousFrame = before.playhead.frame;
      const previousPose = before.livePose;
      const previousHipsY = before.liveHipsY;
      const discarding = hasUnkeyedPose(before, previousFrame);

      deactivateAllIk(ctx);
      gotoFrame(frame);

      const after = ctx.getState();
      if (!discarding || after.playhead.frame === previousFrame) return;

      const nextFrame = after.playhead.frame;
      const nextPose = after.livePose;
      const nextHipsY = after.liveHipsY;
      const restore = (): void => {
        const state = ctx.getState();
        ctx.set({
          playhead: { ...state.playhead, frame: previousFrame },
          livePose: previousPose,
          liveHipsY: previousHipsY,
        });
      };
      const reapply = (): void => {
        const state = ctx.getState();
        ctx.set({
          playhead: { ...state.playhead, frame: nextFrame },
          livePose: nextPose,
          liveHipsY: nextHipsY,
        });
      };
      ctx.history.push({ label: 'Discard unkeyed pose', undo: restore, redo: reapply });
      showToast(ctx, 'info', 'Unkeyed pose discarded — Ctrl+Z to restore.');
    },

    play(): void {
      const state = ctx.getState();
      if (!state.clip || state.playhead.playing) return;
      ctx.set({ playhead: { ...state.playhead, playing: true } });
    },

    pause(): void {
      const state = ctx.getState();
      if (!state.playhead.playing) return;
      ctx.set({ playhead: { ...state.playhead, playing: false } });
    },

    togglePlay(): void {
      const state = ctx.getState();
      if (state.playhead.playing) {
        ctx.set({ playhead: { ...state.playhead, playing: false } });
        return;
      }
      if (!state.clip) return;
      ctx.set({ playhead: { ...state.playhead, playing: true } });
    },

    stop(): void {
      deactivateAllIk(ctx);
      const state = ctx.getState();
      ctx.set({
        playhead: { ...state.playhead, playing: false, frame: 0 },
        ...evaluatedLive(state.clip, 0),
      });
    },

    setPlaybackSpeed(speed): void {
      const state = ctx.getState();
      if (!Number.isFinite(speed)) return;
      const next = Math.min(PLAYBACK_SPEED_MAX, Math.max(PLAYBACK_SPEED_MIN, speed));
      if (state.playhead.speed === next) return;
      ctx.set({ playhead: { ...state.playhead, speed: next } });
    },

    tick(deltaSeconds): void {
      const state = ctx.getState();
      const clip = state.clip;
      if (!clip || !state.playhead.playing) return;
      if (!Number.isFinite(deltaSeconds) || deltaSeconds === 0) return;

      const advanced = state.playhead.frame + deltaSeconds * state.playhead.speed * clip.fps;
      let frame: number;
      let playing = true;
      if (state.playhead.loop) {
        frame = wrapFrame(clip, advanced, true);
      } else if (advanced >= clip.durationFrames) {
        frame = clip.durationFrames;
        playing = false;
      } else {
        frame = wrapFrame(clip, advanced, false);
      }

      // Playback re-derives BOTH live slices every tick — this call is the only reason a crouch
      // clip moves the pelvis while it plays.
      ctx.set({
        playhead: { ...state.playhead, frame, playing },
        ...evaluatedLive(clip, frame),
      });
    },

    setSnap(snap): void {
      const timeline = ctx.getState().timeline;
      if (timeline.snap === snap) return;
      ctx.set({ timeline: { ...timeline, snap } });
    },

    setZoom(zoom): void {
      const timeline = ctx.getState().timeline;
      const next = Number.isFinite(zoom)
        ? Math.min(TIMELINE_ZOOM_MAX, Math.max(TIMELINE_ZOOM_MIN, zoom))
        : timeline.zoom;
      if (timeline.zoom === next) return;
      ctx.set({ timeline: { ...timeline, zoom: next } });
    },

    setScrollFrame(frame): void {
      const timeline = ctx.getState().timeline;
      const next = Number.isFinite(frame) ? Math.max(0, frame) : 0;
      if (timeline.scrollFrame === next) return;
      ctx.set({ timeline: { ...timeline, scrollFrame: next } });
    },

    setVisibleBones(filter): void {
      const timeline = ctx.getState().timeline;
      if (timeline.visibleBones === filter) return;
      ctx.set({ timeline: { ...timeline, visibleBones: filter } });
    },
  };
}

/** Clip length in seconds — handy for the transport display. */
export function clipLengthSeconds(state: EditorState): number {
  return state.clip ? clipDurationSeconds(state.clip) : 0;
}
